// activeLeg — derive the active flight-plan leg from current GPS fix.
//
// "Active leg" = the segment from the most-recently-passed waypoint to
// the next-upcoming waypoint. Map renderer + instrument bar both need
// this concept, so it lives here.
//
// The math is the same heuristic instrumentBar.tsx has used since 2026-
// 05-02: pick the leg whose TO endpoint is closest to the fix AND is
// at least 0.3 nm ahead (avoids never advancing past a passed fix).
//
// Returns:
//   - `nextWpIdx` = index of the upcoming waypoint (1..route.length-1)
//                   when in flight; 0 for a 1-point direct-to;
//                   -1 when not enough data.
//   - `activeLegIndex` = nextWpIdx - 1, the index of the FROM-side
//                        endpoint. -1 means no active leg yet.
//   - Legs with index < activeLegIndex are "flown" (behind us).
//   - The leg AT activeLegIndex is "active".
//   - Legs with index > activeLegIndex are "unflown" (ahead).

import { greatCircleNm } from './geo';

export interface ActiveLegInfo {
  nextWpIdx: number;
  activeLegIndex: number;
}

interface MinPoint {
  lat: number;
  lon: number;
}

export function computeActiveLeg(
  fix: { lat: number; lon: number } | null,
  route: ReadonlyArray<MinPoint>,
): ActiveLegInfo {
  if (!fix) return { nextWpIdx: -1, activeLegIndex: -1 };
  if (route.length === 0) return { nextWpIdx: -1, activeLegIndex: -1 };
  if (route.length === 1) return { nextWpIdx: 0, activeLegIndex: -1 };

  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = 1; i < route.length; i++) {
    const w = route[i];
    const d = greatCircleNm(fix.lat, fix.lon, w.lat, w.lon);
    // Need to be at least 0.3 nm ahead — otherwise we'd never advance
    // past a fix we already crossed.
    if (d < bestDist && d > 0.3) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return { nextWpIdx: bestIdx, activeLegIndex: bestIdx > 0 ? bestIdx - 1 : -1 };
}
